/* eslint-disable no-empty */
/* eslint-disable react/no-unknown-property */
import React, { useState, useContext, useEffect, useRef, useMemo, useCallback } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { AppContext } from "../../AppContextProvider";
import { readFileCached } from "../../utils/previewCache";
import { WalletProviderContext } from "../../walletprovider/WalletProvider";
import { DwMpcWalletBackendProviderContext } from "../../mpcwalletprovider/DwMpcWalletBackendProvider";
import OfficeFilePreview from "../OfficeFilePreview";
import DwinityLoader from "../DwinityLoader";
import PreviewModal from "../../modals/PreviewModal";
import PdfReactViewer from "../PdfJsPreview";
import {
  getFileIcon,
  getFileExtension,
  classifyExt,
  formatFileSize,
  formatDate,
  getParentPath,
  norm,
  getItemName,
  getItemKind,
  itemMatchesSearch,
  itemMatchesKind,
  KIND_LABELS,
  KIND_ORDER,
} from "./UnifiedFileIcons";

const MAX_PREVIEW_BYTES = 60 * 1024 * 1024;
const PREVIEW_TIMEOUT_MS = 15000;
const LAST_PATH_KEY = "fm:lastPath";
const LS_SEARCH_Q_KEY = "fm:searchQ";
const LS_KIND_FILTER_KEY = "fm:kindFilter";
const LS_VIEW_MODE_KEY = "fm:viewMode";

const pickName = (node) => node?.properties?.fileName || node?.name || node?.label || "";
const pickMime = (node) => (node?.properties?.fileMimeType || "").toLowerCase();

const FolderTreeItem = React.memo(({ folder, allFolders, currentPath, onNavigate, depth = 0 }) => {
  const [expanded, setExpanded] = useState(() => {
    return currentPath === folder.path || currentPath.startsWith(folder.path + "/");
  });
  const [tooltip, setTooltip] = useState(null);
  const btnRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (currentPath === folder.path || currentPath.startsWith(folder.path + "/")) {
      setExpanded(true);
    }
  }, [currentPath, folder.path]);

  const children = useMemo(
    () => allFolders.filter((f) => getParentPath(f.path) === folder.path),
    [allFolders, folder.path]
  );
  const isActive = currentPath === folder.path;
  const hasChildren = children.length > 0;

  const handleMouseEnter = useCallback((e) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    const btn = btnRef.current;
    if (!btn) return;
    const sidebar = btn.closest('.uf-sidebar');
    if (!sidebar) return;
    const sidebarRect = sidebar.getBoundingClientRect();
    const btnRect = btn.getBoundingClientRect();
    if (btnRect.right <= sidebarRect.right + 2) return;
    timerRef.current = setTimeout(() => {
      const r = btn.getBoundingClientRect();
      setTooltip({ text: folder.name, x: r.left + 30, y: r.bottom + 2 });
    }, 500);
  }, [folder.name]);

  const handleMouseLeave = useCallback(() => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    setTooltip(null);
  }, []);

  return (
    <div className="uf-tree-item">
      <button
        ref={btnRef}
        className={`uf-tree-btn${isActive ? " active" : ""}`}
        style={{ paddingLeft: 8 + depth * 16 }}
        onClick={() => onNavigate(folder.path)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {hasChildren ? (
          <span
            className="uf-tree-arrow"
            onClick={(e) => { e.stopPropagation(); setExpanded((p) => !p); }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" style={{ transform: expanded ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.15s" }}>
              <path d="M9 6L15 12L9 18" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        ) : (
          <span className="uf-tree-arrow" style={{ visibility: "hidden" }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
              <path d="M9 6L15 12L9 18" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        )}
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
          <path d="M2 6C2 4.89543 2.89543 4 4 4H9L11 6H20C21.1046 6 22 6.89543 22 8V18C22 19.1046 21.1046 20 20 20H4C2.89543 20 2 19.1046 2 18V6Z" fill={isActive ? "#6c5ce7" : "#f1c40f"} />
          <path d="M2 8H22V18C22 19.1046 21.1046 20 20 20H4C2.89543 20 2 19.1046 2 18V8Z" fill={isActive ? "#5b4cdb" : "#f39c12"} opacity="0.6" />
        </svg>
        <span className="uf-tree-name">{folder.name}</span>
      </button>
      {tooltip && ReactDOM.createPortal(
        <div className="uf-tree-tooltip" style={{ left: tooltip.x, top: tooltip.y }}>
          {tooltip.text}
        </div>,
        document.body
      )}
      {hasChildren && expanded && (
        <div className="uf-tree-children">
          {children.map((child) => (
            <FolderTreeItem
              key={child.id || child.path}
              folder={child}
              allFolders={allFolders}
              currentPath={currentPath}
              onNavigate={onNavigate}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
});

export default function FilemanagementComponent(props) {
  const { t, i18n } = useTranslation();
  const context = useContext(AppContext);
  const { selectedWallet } = useContext(WalletProviderContext);
  const { storageProps } = useContext(DwMpcWalletBackendProviderContext);

  const [currentPath, setCurrentPath] = useState(() => {
    try {
      return sessionStorage.getItem(LAST_PATH_KEY) || "/User Files";
    } catch {
      return "/User Files";
    }
  });

  const [searchQ, setSearchQ] = useState(() => {
    try { return sessionStorage.getItem(LS_SEARCH_Q_KEY) || ""; } catch { return ""; }
  });
  const [kindFilter, setKindFilter] = useState(() => {
    try { return sessionStorage.getItem(LS_KIND_FILTER_KEY) || "all"; } catch { return "all"; }
  });
  const [viewMode, setViewMode] = useState(() => {
    try { return sessionStorage.getItem(LS_VIEW_MODE_KEY) || "list"; } catch { return "list"; }
  });
  const [sortKey, setSortKey] = useState("name");
  const [sortAsc, setSortAsc] = useState(true);
  const [showSortMenu, setShowSortMenu] = useState(false);
  const sortLabel = sortKey === "name" ? t('files.explorer.name', { defaultValue: 'Name' }) : sortKey === "date" ? t('files.explorer.date', { defaultValue: 'Date' }) : t('files.explorer.size', { defaultValue: 'Size' });

  const [preparing, setPreparing] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewErr, setPreviewErr] = useState("");
  const [tooLarge, setTooLarge] = useState(null);
  const [blobOrUrl, setBlobOrUrl] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewUrlIsObject, setPreviewUrlIsObject] = useState(false);
  const [currentMeta, setCurrentMeta] = useState({ name: "", node: null, mime: "" });
  const [previewMeta, setPreviewMeta] = useState(null);
  const [contextMenuItem, setContextMenuItem] = useState(null);
  const [contextMenuPos, setContextMenuPos] = useState({ x: 0, y: 0 });

  const rootRef = useRef(null);

  useEffect(() => {
    try { sessionStorage.setItem(LAST_PATH_KEY, currentPath); } catch {}
  }, [currentPath]);
  useEffect(() => {
    try { sessionStorage.setItem(LS_SEARCH_Q_KEY, searchQ); } catch {}
  }, [searchQ]);
  useEffect(() => {
    try { sessionStorage.setItem(LS_KIND_FILTER_KEY, kindFilter); } catch {}
  }, [kindFilter]);
  useEffect(() => {
    try { sessionStorage.setItem(LS_VIEW_MODE_KEY, viewMode); } catch {}
  }, [viewMode]);

  useEffect(() => {
    if (!preparing && (blobOrUrl || tooLarge || previewErr)) setPreviewOpen(true);
  }, [preparing, blobOrUrl, tooLarge, previewErr]);

  useEffect(() => {
    if (!blobOrUrl) {
      if (previewUrl && previewUrlIsObject) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
      setPreviewUrlIsObject(false);
      return;
    }
    let created = false;
    let nextUrl = "";
    if (blobOrUrl instanceof Blob || blobOrUrl instanceof File) {
      nextUrl = URL.createObjectURL(blobOrUrl);
      created = true;
    } else {
      nextUrl = String(blobOrUrl);
    }
    setPreviewUrl(nextUrl);
    setPreviewUrlIsObject(created);
    return () => { if (created) URL.revokeObjectURL(nextUrl); };
  }, [blobOrUrl]);

  const allFolders = useMemo(
    () => (props.files || []).filter((f) => f.isDirectory),
    [props.files]
  );

  const rootFolders = useMemo(
    () => allFolders.filter((f) => {
      const parent = getParentPath(f.path);
      return !parent || !allFolders.some((ff) => ff.path === parent);
    }),
    [allFolders]
  );

  useEffect(() => {
    if (allFolders.length > 0 && !allFolders.some((f) => f.path === currentPath)) {
      const root = rootFolders[0];
      if (root) setCurrentPath(root.path);
    }
  }, [allFolders, currentPath, rootFolders]);

  const directChildren = useMemo(() => {
    return (props.files || []).filter((item) => {
      if (item.path === currentPath) return false;
      return getParentPath(item.path) === currentPath;
    });
  }, [props.files, currentPath]);

  const filteredAndSorted = useMemo(() => {
    let items = directChildren;
    if (searchQ) {
      items = items.filter((item) => itemMatchesSearch(item, searchQ));
    }
    if (kindFilter !== "all") {
      items = items.filter((item) => itemMatchesKind(item, kindFilter));
    }
    const folders = items.filter((i) => i.isDirectory);
    const files = items.filter((i) => !i.isDirectory);
    const sortFn = (a, b) => {
      let cmp = 0;
      if (sortKey === "name") {
        cmp = (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" });
      } else if (sortKey === "date") {
        const da = new Date(a.updatedAt || 0).getTime();
        const db = new Date(b.updatedAt || 0).getTime();
        cmp = da - db;
      } else if (sortKey === "size") {
        cmp = (a.size || 0) - (b.size || 0);
      }
      return sortAsc ? cmp : -cmp;
    };
    folders.sort(sortFn);
    files.sort(sortFn);
    return [...folders, ...files];
  }, [directChildren, searchQ, kindFilter, sortKey, sortAsc]);

  const availableKinds = useMemo(() => {
    const counts = {};
    directChildren.forEach((item) => {
      const kind = getItemKind(item);
      counts[kind] = (counts[kind] || 0) + 1;
    });
    const base = KIND_ORDER.filter((k) => counts[k] > 0).map((k) => ({
      value: k,
      label: `${KIND_LABELS[k]} (${counts[k]})`,
    }));
    return [{ value: "all", label: t('files.explorer.allTypes', { defaultValue: 'All types' }) }, ...base];
  }, [directChildren, t]);

  const handleSort = useCallback((key) => {
    setSortKey((prev) => {
      if (prev === key) setSortAsc((a) => !a);
      else setSortAsc(true);
      return key;
    });
    setShowSortMenu(false);
  }, []);

  const preparePreview = useCallback(async (node) => {
    if (!node) return;
    try {
      setPreparing(true);
      setPreviewErr("");
      setBlobOrUrl(null);
      setTooLarge(null);
      setPreviewMeta({
        name: node?.properties?.fileName || node?.name || node?.label,
        size: node?.properties?.fileSize ?? node?.size,
        mimeType: node?.properties?.fileMimeType,
        storageId: node?.properties?.fileStorageId,
        createdAt: node?.properties?.creationTime,
        updatedAt: node?.updatedAt,
        path: node?.path,
        id: node?.id,
        node,
      });
      const sizeNum = Number(node?.properties?.fileSize || 0) || 0;
      const name = pickName(node);
      const mime = pickMime(node);
      setCurrentMeta({ name, node, mime });
      if (sizeNum > MAX_PREVIEW_BYTES) {
        setTooLarge({ name, size: sizeNum });
        return;
      }
      const result = await readFileCached(
        selectedWallet?.provider,
        storageProps,
        node?.properties?.fileStorageId,
        node?.properties?.fileSize,
      );
      let value;
      if (result instanceof Blob || result instanceof File) value = result;
      else if (typeof result === "string") value = result;
      else {
        const controller = new AbortController();
        const to = setTimeout(() => controller.abort("timeout"), PREVIEW_TIMEOUT_MS);
        const res = await fetch(result, { signal: controller.signal });
        clearTimeout(to);
        if (!res.ok) throw new Error(`Failed to download file (${res.status})`);
        value = await res.blob();
      }
      setBlobOrUrl(value);
    } catch (err) {
      console.error("preparePreview error:", err);
      setPreviewErr("Failed to prepare preview. Please try again.");
    } finally {
      setPreparing(false);
    }
  }, [selectedWallet, storageProps]);

  const handleItemClick = useCallback((item) => {
    if (item.isDirectory) {
      setCurrentPath(item.path);
    } else if (item.node) {
      preparePreview(item.node);
    }
  }, [preparePreview]);

  const handleDownload = useCallback((item) => {
    setContextMenuItem(null);
    try {
      if (item.isDirectory) {
        props.setActuelleDataOwnerComponent?.(item.node);
        props.setIsMultiDownload?.(true);
        props.setIsFolderDownload?.(true);
        context?.isProgress?.(true);
      } else {
        (props.singleFileDownload || props.readFileTest)?.(item.node);
      }
    } catch (err) {
      console.error("download error:", err);
    }
  }, [props, context]);

  const handleDelete = useCallback((item) => {
    setContextMenuItem(null);
    try {
      if (item.isDirectory) {
        props.setActuelleDataOwnerComponent?.(item.node);
        props.setIsWarningTicket3?.(true);
      } else {
        sessionStorage.setItem("selectedNodeList", JSON.stringify([item.node]));
        props.setIsWarningTicket?.(true);
      }
    } catch (err) {
      console.error("delete error:", err);
    }
  }, [props]);

  const handleContextMenu = useCallback((e, item) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenuItem(item);
    setContextMenuPos({ x: e.clientX, y: e.clientY });
  }, []);

  useEffect(() => {
    if (!contextMenuItem) return;
    const close = () => setContextMenuItem(null);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [contextMenuItem]);

  const pathSegments = useMemo(() => {
    if (!currentPath) return [];
    const parts = currentPath.split("/").filter(Boolean);
    return parts.map((part, i) => ({
      name: part === "User Files" ? t('files.explorer.myFiles', { defaultValue: 'My Files' }) : part,
      path: "/" + parts.slice(0, i + 1).join("/"),
    }));
  }, [currentPath, t]);

  const ContextAwarePreview = () => {
    if (previewErr) return <div style={{ color: "var(--text-brand-secondary)" }}>{previewErr}</div>;
    if (tooLarge) {
      return (
        <div style={{ color: "var(--text-brand-secondary)" }}>
          <div style={{ fontWeight: 700, marginBottom: 8 }}>Preview not available for large files</div>
          <div><strong>Selected file:</strong> {tooLarge.name} ({formatFileSize(tooLarge.size)})</div>
          <div><strong>Preview limit:</strong> {formatFileSize(MAX_PREVIEW_BYTES)}</div>
          <div style={{ marginTop: 8 }}>You can still download this file from the context menu.</div>
        </div>
      );
    }
    if (!blobOrUrl || !previewUrl) return <div style={{ textAlign: "center", padding: 24 }}>Loading…</div>;
    const name = (currentMeta.name || "").toLowerCase();
    const mimeFromNode = (currentMeta.mime || "").toLowerCase();
    const isBlob = blobOrUrl instanceof Blob || blobOrUrl instanceof File;
    const blobMime = isBlob ? (blobOrUrl.type || "").toLowerCase() : "";
    const effMime = (mimeFromNode || blobMime).toLowerCase();
    const isImg = /\.(jpg|jpeg|png|gif|webp)$/i.test(name) || effMime.startsWith("image/");
    const isPdf = /\.pdf$/i.test(name) || effMime === "application/pdf";
    const isVideo = /\.mp4$/i.test(name) || effMime.startsWith("video/");
    if (isImg) return <img src={previewUrl} alt="" style={{ maxWidth: "100%", height: "auto" }} />;
    if (isPdf) return <PdfReactViewer file={previewUrl} initialScale={1.3} />;
    if (isVideo) return <video src={previewUrl} controls style={{ width: "100%", maxHeight: "70vh" }} preload="metadata" />;
    return <OfficeFilePreview file={blobOrUrl} filename={currentMeta.name} mime={effMime} />;
  };

  return (
    <div ref={rootRef} className="uf-explorer-desktop">
      <div className="uf-sidebar">
        <div className="uf-sidebar-header">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M3 7V17C3 18.1046 3.89543 19 5 19H19C20.1046 19 21 18.1046 21 17V9C21 7.89543 20.1046 7 19 7H12L10 5H5C3.89543 5 3 5.89543 3 7Z" stroke="#6c5ce7" strokeWidth="1.5" fill="none" />
          </svg>
          <span>{t('files.explorer.fileExplorer', { defaultValue: 'Explorer' })}</span>
        </div>
        <div className="uf-sidebar-tree">
          {rootFolders.map((folder) => (
            <FolderTreeItem
              key={folder.id || folder.path}
              folder={folder}
              allFolders={allFolders}
              currentPath={currentPath}
              onNavigate={setCurrentPath}
            />
          ))}
        </div>
      </div>

      <div className="uf-main">
        <div className="uf-toolbar">
          <div className="uf-search-bar">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <circle cx="11" cy="11" r="7" stroke="#fff" strokeWidth="2" />
              <path d="M16 16L20 20" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <input
              type="text"
              placeholder={t('files.explorer.searchPlaceholder', { defaultValue: 'Search files...' })}
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
            />
            {searchQ && (
              <button className="uf-search-clear" onClick={() => setSearchQ("")}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6L18 18" stroke="#888" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
            )}
          </div>

          <div className="uf-toolbar-right">
            {props.onCreateFolder && (
              <button
                className="uf-new-folder-btn"
                onClick={() => props.onCreateFolder(currentPath)}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path d="M2 6C2 4.89543 2.89543 4 4 4H9L11 6H20C21.1046 6 22 6.89543 22 8V18C22 19.1046 21.1046 20 20 20H4C2.89543 20 2 19.1046 2 18V6Z" fill="currentColor" />
                  <path d="M12 10V16M9 13H15" stroke="var(--bg-primary, #1a1a2e)" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
                <span>{t('files.actions.newFolder', { defaultValue: 'New Folder' })}</span>
              </button>
            )}

            <select
              className="uf-kind-select"
              value={kindFilter}
              onChange={(e) => setKindFilter(e.target.value)}
            >
              {availableKinds.map((k) => (
                <option key={k.value} value={k.value}>{k.label}</option>
              ))}
            </select>

            <div className="uf-sort-wrapper">
              <button className="uf-toolbar-btn" onClick={() => setShowSortMenu((p) => !p)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path d="M3 6H21M3 12H15M3 18H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
                <span>{sortLabel}</span>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" style={{ transform: sortAsc ? "rotate(0)" : "rotate(180deg)", transition: "transform 0.15s" }}>
                  <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
              {showSortMenu && (
                <>
                  <div className="uf-sort-overlay" onClick={() => setShowSortMenu(false)} />
                  <div className="uf-sort-menu">
                    <button className={sortKey === "name" ? "active" : ""} onClick={() => handleSort("name")}>
                      {t('files.explorer.name', { defaultValue: 'Name' })}
                    </button>
                    <button className={sortKey === "date" ? "active" : ""} onClick={() => handleSort("date")}>
                      {t('files.explorer.date', { defaultValue: 'Date' })}
                    </button>
                    <button className={sortKey === "size" ? "active" : ""} onClick={() => handleSort("size")}>
                      {t('files.explorer.size', { defaultValue: 'Size' })}
                    </button>
                  </div>
                </>
              )}
            </div>

            <div className="uf-view-toggle">
              <button className={`uf-view-btn${viewMode === "list" ? " active" : ""}`} onClick={() => setViewMode("list")}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <rect x="3" y="4" width="18" height="2" rx="1" fill="currentColor" />
                  <rect x="3" y="11" width="18" height="2" rx="1" fill="currentColor" />
                  <rect x="3" y="18" width="18" height="2" rx="1" fill="currentColor" />
                </svg>
              </button>
              <button className={`uf-view-btn${viewMode === "grid" ? " active" : ""}`} onClick={() => setViewMode("grid")}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <rect x="3" y="3" width="8" height="8" rx="1.5" fill="currentColor" />
                  <rect x="13" y="3" width="8" height="8" rx="1.5" fill="currentColor" />
                  <rect x="3" y="13" width="8" height="8" rx="1.5" fill="currentColor" />
                  <rect x="13" y="13" width="8" height="8" rx="1.5" fill="currentColor" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        <div className="uf-breadcrumb">
          {pathSegments.map((seg, i) => (
            <React.Fragment key={seg.path}>
              {i > 0 && <span className="uf-breadcrumb-sep">/</span>}
              <button
                className={`uf-breadcrumb-item${i === pathSegments.length - 1 ? " active" : ""}`}
                onClick={() => setCurrentPath(seg.path)}
              >
                {seg.name}
              </button>
            </React.Fragment>
          ))}
          <span className="uf-item-count">
            {filteredAndSorted.length} {t('files.explorer.items', { defaultValue: 'items' })}
          </span>
        </div>

        {kindFilter !== "all" && (
          <div className="uf-filter-tag">
            <span>{KIND_LABELS[kindFilter] || kindFilter}</span>
            <button onClick={() => setKindFilter("all")}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        )}

        <div className="uf-content">
          {preparing && (
            <div className="uf-loading-overlay">
              <div className="uf-loading-box">
                <DwinityLoader size={24} fullScreen={false} inline />
                <span>Preparing preview…</span>
              </div>
            </div>
          )}

          {filteredAndSorted.length === 0 ? (
            <div className="uf-empty">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                <path d="M2 6C2 4.89543 2.89543 4 4 4H9L11 6H20C21.1046 6 22 6.89543 22 8V18C22 19.1046 21.1046 20 20 20H4C2.89543 20 2 19.1046 2 18V6Z" fill="#2a2a3e" />
              </svg>
              <p>{searchQ
                ? t('files.explorer.noSearchResults', { defaultValue: 'No files match your search' })
                : t('files.explorer.folderEmpty', { defaultValue: 'This folder is empty' })}
              </p>
            </div>
          ) : viewMode === "list" ? (
            <div className="uf-file-list">
              {filteredAndSorted.map((item) => (
                <button
                  key={item.id || item.path}
                  className="uf-file-item-list"
                  onClick={() => handleItemClick(item)}
                  onContextMenu={(e) => handleContextMenu(e, item)}
                >
                  <div className="uf-file-item-icon">{getFileIcon(item, 32)}</div>
                  <div className="uf-file-item-info">
                    <span className="uf-file-item-name">{item.name}</span>
                    <span className="uf-file-item-meta">
                      {item.isDirectory
                        ? t('files.explorer.folder', { defaultValue: 'Folder' })
                        : formatFileSize(item.size)}
                      {" · "}
                      {formatDate(item.updatedAt, t, i18n.language)}
                    </span>
                  </div>
                  {item.isDirectory ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="uf-file-item-chevron">
                      <path d="M9 6L15 12L9 18" stroke="#555" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="uf-file-item-chevron" style={{ opacity: 0.5 }}>
                      <path d="M12 16V4M12 16L8 12M12 16L16 12" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M4 18H20" stroke="#888" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                  )}
                </button>
              ))}
            </div>
          ) : (
            <div className="uf-file-grid">
              {filteredAndSorted.map((item) => (
                <button
                  key={item.id || item.path}
                  className="uf-file-item-grid"
                  onClick={() => handleItemClick(item)}
                  onContextMenu={(e) => handleContextMenu(e, item)}
                >
                  <div className="uf-file-item-grid-icon">{getFileIcon(item, 40)}</div>
                  <span className="uf-file-item-grid-name">{item.name}</span>
                  <span className="uf-file-item-grid-meta">
                    {item.isDirectory
                      ? t('files.explorer.folder', { defaultValue: 'Folder' })
                      : formatFileSize(item.size)}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        {contextMenuItem && (
          <div
            className="uf-context-menu"
            style={{ top: contextMenuPos.y, left: contextMenuPos.x }}
          >
            {!contextMenuItem.isDirectory && (
              <button onClick={() => { preparePreview(contextMenuItem.node); setContextMenuItem(null); }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
                  <path d="M2 12C4 7 8 4 12 4S20 7 22 12C20 17 16 20 12 20S4 17 2 12Z" stroke="currentColor" strokeWidth="2" fill="none" />
                </svg>
                <span>{t('files.explorer.open', { defaultValue: 'Open' })}</span>
              </button>
            )}
            <button onClick={() => handleDownload(contextMenuItem)}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path d="M12 4V16M12 16L8 12M12 16L16 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <path d="M4 18H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
              <span>{t('files.explorer.download', { defaultValue: 'Download' })}</span>
            </button>
            <button className="uf-context-delete" onClick={() => handleDelete(contextMenuItem)}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path d="M4 7H20M10 11V17M14 11V17M5 7L6 19C6 20.1046 6.89543 21 8 21H16C17.1046 21 18 20.1046 18 19L19 7M9 7V4C9 3.44772 9.44772 3 10 3H14C14.5523 3 15 3.44772 15 4V7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
              <span>{t('files.explorer.delete', { defaultValue: 'Delete' })}</span>
            </button>
          </div>
        )}
      </div>

      <PreviewModal
        open={previewOpen}
        title={currentMeta?.name || "Preview"}
        onClose={() => {
          setPreviewOpen(false);
          setPreviewErr("");
          setTooLarge(null);
          if (previewUrl && previewUrlIsObject) URL.revokeObjectURL(previewUrl);
          setPreviewUrl(null);
          setPreviewUrlIsObject(false);
          setBlobOrUrl(null);
        }}
        meta={previewMeta}
      >
        <ContextAwarePreview />
      </PreviewModal>
    </div>
  );
}
